import { RegionCode } from '@kaa/api/customers';
import { i18nKeys } from '@kaa/i18n/customers/keys';
import {
  Icon,
  SwActionGroup,
  SwAlert,
  SwButton,
  SwColumn,
  SwIconList,
  SwIconListItem,
  SwTitle,
} from '@kaa/ui-flanders/components';
import get from 'lodash.get';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getConfig } from '../../../../../../../common/config';
import { REGION_NAME } from '../../../../../../constants';
import {
  EventAction,
  EventLabel,
  sendDownloadEventToGTM,
} from '../../../../../../utils/google-analytics';

type RefundPaperConfirmProps = {
  documentBlob: Blob;
  sodexoPostBox: string;
};

export const RefundPaperConfirm = ({
  documentBlob,
  sodexoPostBox,
}: RefundPaperConfirmProps) => {
  const { t } = useTranslation();
  const regionCode = get(getConfig(), 'buildConfig.regionCode') as RegionCode;

  const document = window.URL.createObjectURL(documentBlob);
  const isIE = !!window.navigator.msSaveOrOpenBlob;

  return (
    <>
      <p className="vl-u-display-block vl-u-spacer">
        {t(i18nKeys.refund.paper.confirmation.introduction)}
      </p>

      <SwTitle tagName="h1" tagStyle="h4" className="vl-u-spacer">
        {t(i18nKeys.refund.paper.confirmation.whatHappensNext)}
      </SwTitle>

      <SwIconList>
        <SwIconListItem
          className="vl-u-hr"
          style={{ paddingBottom: '1rem' }}
          icon={Icon.FILE_DOWNLOAD}
          modLarge
        >
          <strong>1.</strong> {t(i18nKeys.refund.paper.howTo.download)}
        </SwIconListItem>
        <SwIconListItem
          className="vl-u-hr vl-typography"
          style={{ paddingBottom: '1rem' }}
          icon={Icon.PRINT}
          modLarge
        >
          <strong>2.</strong> {t(i18nKeys.refund.paper.howTo.print)}
        </SwIconListItem>
        <SwIconListItem
          className="vl-u-hr"
          style={{ paddingBottom: '1rem' }}
          icon={Icon.SCISSORS}
          modLarge
        >
          <strong>3.</strong> {t(i18nKeys.refund.paper.howTo.cut)}
        </SwIconListItem>
        <SwIconListItem
          style={{ paddingBottom: '1rem' }}
          icon={regionCode === 'BE_VLG' ? Icon.ENVELOPE : Icon.SEND_DOCUMENT}
          modLarge
        >
          <strong>4.</strong> {t(i18nKeys.refund.paper.howTo.send)}
          <strong>
            <br />
            {t(i18nKeys.refund.paper.howTo.address.name)}
            <br />
            {t(i18nKeys.refund.paper.howTo.address.street, {
              postalBox: sodexoPostBox,
            })}
            <br />
            {t(i18nKeys.refund.paper.howTo.address.city)}
          </strong>
        </SwIconListItem>
      </SwIconList>

      <SwColumn>
        <SwAlert
          icon={Icon.INFO_CIRCLE}
          title={t(i18nKeys.refund.paper.confirmation.infoTile.title)}
          titleTagName="h2"
        >
          {t(i18nKeys.refund.paper.confirmation.infoTile.message)}
        </SwAlert>
      </SwColumn>

      <SwActionGroup>
        {!isIE ? (
          <SwButton
            tagName="a"
            href={document}
            onClick={() =>
              sendDownloadEventToGTM(
                EventAction.GET_DOCUMENT,
                EventLabel.REFUND_PAPER_LINK,
              )
            }
            download={`${t(i18nKeys.refund.paper.confirmation.fileName, {
              regionName: t(REGION_NAME[regionCode]),
            })}.pdf`}
            modBlock
            modLarge
          >
            {t(i18nKeys.refund.paper.confirmation.cta.getMyDocument)}
          </SwButton>
        ) : (
          <SwButton
            onClick={(e) => {
              e.preventDefault();
              window.navigator.msSaveOrOpenBlob(
                documentBlob,
                `${t(i18nKeys.refund.paper.confirmation.fileName, {
                  regionName: t(REGION_NAME[regionCode]),
                })}.pdf`,
              );

              sendDownloadEventToGTM(
                EventAction.GET_DOCUMENT,
                EventLabel.REFUND_PAPER_LINK,
              );
            }}
            modBlock
            modLarge
          >
            {t(i18nKeys.refund.paper.confirmation.cta.getMyDocument)}
          </SwButton>
        )}
      </SwActionGroup>
    </>
  );
};
